import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Loader2, AlertTriangle, Package, Ban, Eye, TrendingUp } from "lucide-react";
import api from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

interface DashboardTabProps {
	onNavigateToProducts: (filter?: string) => void;
	onOpenProduct: (id: number) => void;
	onNavigateToTab: (tab: string) => void;
}

export default function DashboardTab({ onNavigateToProducts, onOpenProduct, onNavigateToTab }: DashboardTabProps) {
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState<any>(null);
	const [linkStats, setLinkStats] = useState<any[]>([]);
	const [duplicateSummary, setDuplicateSummary] = useState<any>(null);
	const { toast } = useToast();

	useEffect(() => {
		fetchAll();
	}, []);

	const fetchAll = async () => {
		setLoading(true);
		try {
			const [dashRes, linkRes, dupRes] = await Promise.all([
				api.b2cMgmtGetDashboard(),
				api.b2cMgmtGetLinkStats(),
				api.b2cMgmtGetDuplicateSummary(),
			]);
			if (dashRes?.success) setStats(dashRes.data);
			if (linkRes?.success) setLinkStats(linkRes.data);
			if (dupRes?.success) setDuplicateSummary(dupRes.data);
		} catch (error: any) {
			toast({ title: "Error", description: "Failed to load dashboard", variant: "destructive" });
		} finally {
			setLoading(false);
		}
	};

	if (loading) {
		return (
			<div className="flex items-center justify-center h-64">
				<Loader2 className="h-8 w-8 animate-spin text-blue-500" />
				<span className="ml-2 text-gray-500 dark:text-gray-400">Loading dashboard...</span>
			</div>
		);
	}

	if (!stats) return null;

	const summary = stats.summary;
	const mdmDist = stats.mdm_category_distribution || [];
	const uncategorizedCount = mdmDist.find((d: any) => d.category === "NO CATEGORY")?.count || 0;

	return (
		<div className="space-y-6">
			{/* Row 1: Summary Cards */}
			<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
				<SummaryCard label="Total Products" value={summary.total} color="blue" />
				<SummaryCard label="Active" value={summary.active} color="green" />
				<SummaryCard label="Inactive" value={summary.inactive} color="gray" />
				<SummaryCard label="Banned" value={summary.banned} color="red" />
				<SummaryCard label="Not For Sale" value={summary.not_for_sale} color="orange" />
				<SummaryCard label="Discontinued" value={summary.discontinued} color="yellow" />
			</div>

			{/* Row 2: Data Quality Alerts */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<AlertCard
					icon={<AlertTriangle className="h-5 w-5 text-red-500" />}
					label="Uncategorized"
					value={uncategorizedCount}
					description="Products with no MDM category"
					onClick={() => onNavigateToProducts("no_category")}
					variant="red"
				/>
				<AlertCard
					icon={<TrendingUp className="h-5 w-5 text-orange-500" />}
					label="MRP Discrepancies"
					value="Review"
					description="Entity vs catalog MRP mismatches"
					onClick={() => onNavigateToTab("data-review")}
					variant="orange"
				/>
				<AlertCard
					icon={<Package className="h-5 w-5 text-purple-500" />}
					label="Suspect Mappings"
					value={duplicateSummary?.total_products_with_duplicates || 0}
					description="Review in Data Review queue"
					onClick={() => onNavigateToTab("data-review")}
					variant="purple"
				/>
				<AlertCard
					icon={<Ban className="h-5 w-5 text-gray-500" />}
					label="MRP = 0"
					value={stats.mrp_zero}
					description="Products with zero or null MRP"
					onClick={() => onNavigateToProducts("mrp_zero")}
					variant="gray"
				/>
			</div>

			{/* Row 3: MDM Category Distribution + Flag Distribution */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
				{/* MDM Category Distribution */}
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="text-base dark:text-white">MDM Category Distribution</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-3">
							{mdmDist.map((item: any) => {
								const pct = summary.total > 0 ? (item.count / summary.total) * 100 : 0;
								const isUncategorized = item.category === "NO CATEGORY";
								return (
									<div key={item.category} className="flex items-center gap-3">
										<div className="w-32 text-sm truncate dark:text-gray-300">
											<Badge variant={isUncategorized ? "destructive" : "secondary"} className="text-xs">
												{item.category}
											</Badge>
										</div>
										<div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-5 overflow-hidden">
											<div
												className={`h-5 rounded-full ${isUncategorized ? "bg-red-400" : "bg-blue-400"}`}
												style={{ width: `${Math.max(pct, 1)}%` }}
											/>
										</div>
										<div className="w-20 text-right text-sm text-gray-600 dark:text-gray-400">
											{item.count.toLocaleString()} ({pct.toFixed(1)}%)
										</div>
									</div>
								);
							})}
						</div>
					</CardContent>
				</Card>

				{/* Flag Distribution */}
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="text-base dark:text-white">Flag Distribution</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-3">
							{[
								{ label: "is_drug", count: stats.flags.is_drug, total: summary.total },
								{ label: "prescription_required", count: stats.flags.prescription_required, total: summary.total },
								{ label: "is_discount_applicable", count: stats.flags.is_discount_applicable, total: summary.total },
								{ label: "is_otc", count: stats.flags.is_otc, total: summary.total },
								{ label: "banned_for_sale", count: summary.banned, total: summary.total },
								{ label: "not_for_sale", count: summary.not_for_sale, total: summary.total },
							].map((flag) => {
								const pct = flag.total > 0 ? (flag.count / flag.total) * 100 : 0;
								return (
									<div key={flag.label} className="flex items-center gap-3">
										<div className="w-44 text-sm font-mono dark:text-gray-300">{flag.label}</div>
										<div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-4 overflow-hidden">
											<div
												className="h-4 rounded-full bg-indigo-400"
												style={{ width: `${Math.max(pct, 0.5)}%` }}
											/>
										</div>
										<div className="w-28 text-right text-xs text-gray-600 dark:text-gray-400">
											{flag.count.toLocaleString()} / {flag.total.toLocaleString()}
										</div>
									</div>
								);
							})}
						</div>
					</CardContent>
				</Card>
			</div>

			{/* Row 4: Branch Linkage Health */}
			{linkStats.length > 0 && (
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="text-base dark:text-white">Branch Linkage Health</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="overflow-x-auto">
							<table className="w-full text-sm">
								<thead>
									<tr className="border-b dark:border-gray-700">
										<th className="text-left py-2 px-3 dark:text-gray-300">Branch ID</th>
										<th className="text-right py-2 px-3 dark:text-gray-300">Total MEPs</th>
										<th className="text-right py-2 px-3 dark:text-gray-300">Linked</th>
										<th className="text-right py-2 px-3 dark:text-gray-300">Unlinked</th>
										<th className="text-right py-2 px-3 dark:text-gray-300">Link Rate</th>
									</tr>
								</thead>
								<tbody>
									{linkStats.map((stat: any) => (
										<tr key={stat.branch_id} className="border-b dark:border-gray-700/50 hover:bg-gray-50 dark:hover:bg-gray-700/50">
											<td className="py-2 px-3 font-medium dark:text-gray-200">{stat.branch_id}</td>
											<td className="text-right py-2 px-3 dark:text-gray-300">{stat.total.toLocaleString()}</td>
											<td className="text-right py-2 px-3 text-green-600 dark:text-green-400">{stat.linked.toLocaleString()}</td>
											<td className="text-right py-2 px-3 text-red-600 dark:text-red-400">{stat.unlinked.toLocaleString()}</td>
											<td className="text-right py-2 px-3">
												<Badge variant={stat.link_rate > 80 ? "default" : "destructive"} className="text-xs">
													{stat.link_rate}%
												</Badge>
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</CardContent>
				</Card>
			)}

			{/* Row 5: Recent Changes */}
			{stats.recent_changes?.length > 0 && (
				<Card className="dark:bg-gray-800 dark:border-gray-700">
					<CardHeader>
						<CardTitle className="text-base dark:text-white">Recent Changes</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-2">
							{stats.recent_changes.map((change: any) => (
								<div key={change.id} className="flex items-center justify-between py-2 border-b dark:border-gray-700/50 last:border-0">
									<div className="flex-1">
										<span className="text-sm dark:text-gray-200">
											<span className="font-medium">{change.changed_by_name || change.changed_by_email}</span>
											{" changed "}
											<span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-1 rounded">{change.field_name}</span>
											{" on product "}
											<Button
												variant="link"
												className="p-0 h-auto text-blue-600 dark:text-blue-400"
												onClick={() => onOpenProduct(change.med_product_id)}
											>
												#{change.med_product_id}
											</Button>
										</span>
										{change.old_value && change.new_value && (
											<span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
												{change.old_value} → {change.new_value}
											</span>
										)}
									</div>
									<div className="text-xs text-gray-400 dark:text-gray-500 ml-4 whitespace-nowrap">
										{new Date(change.created_at).toLocaleString()}
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}
		</div>
	);
}

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
	const colorMap: Record<string, string> = {
		blue: "border-l-blue-500",
		green: "border-l-green-500",
		gray: "border-l-gray-400",
		red: "border-l-red-500",
		orange: "border-l-orange-500",
		yellow: "border-l-yellow-500",
	};

	return (
		<Card className={`border-l-4 ${colorMap[color] || "border-l-blue-500"} dark:bg-gray-800 dark:border-gray-700`}>
			<CardContent className="p-4">
				<p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
				<p className="text-2xl font-bold dark:text-white">{value.toLocaleString()}</p>
			</CardContent>
		</Card>
	);
}

function AlertCard({
	icon, label, value, description, onClick, variant,
}: {
	icon: React.ReactNode;
	label: string;
	value: string | number;
	description: string;
	onClick: () => void;
	variant: string;
}) {
	const bgMap: Record<string, string> = {
		red: "bg-red-50 dark:bg-red-900/20 hover:bg-red-100 dark:hover:bg-red-900/30",
		orange: "bg-orange-50 dark:bg-orange-900/20 hover:bg-orange-100 dark:hover:bg-orange-900/30",
		purple: "bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30",
		gray: "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700",
	};

	return (
		<Card
			className={`cursor-pointer transition-colors ${bgMap[variant] || bgMap.gray} dark:border-gray-700`}
			onClick={onClick}
		>
			<CardContent className="p-4 flex items-start gap-3">
				{icon}
				<div>
					<p className="text-sm font-medium dark:text-gray-200">{label}</p>
					<p className="text-xl font-bold dark:text-white">{typeof value === "number" ? value.toLocaleString() : value}</p>
					<p className="text-xs text-gray-500 dark:text-gray-400">{description}</p>
				</div>
			</CardContent>
		</Card>
	);
}
